import { useProducts } from '@/features/quotes/hooks/common/use-products.hook';
import type { Broker } from '@/services/brokers/types';
import { InspectionStatus } from '@/shared/enums/inspection-status.enum';
import { PortalStatus } from '@/shared/enums/portal-status.enum';
import { useDataTablePageSync } from '@/shared/hooks';
import {
  DateRangeFilterField,
  SearchFilterField,
  SelectFilterField,
} from '@/shared/ui';
import { Page, PageHeader } from '@/shared/ui/page';
import {
  f,
  formatDateToBrazil,
  getLastInspectionStatusKey,
  isInspectionTerminalStatus,
  useFilters
} from '@/shared/utils';
import { Avatar, DataTable, Menu, Pagination, Tag } from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import { CalendarDays, History, SquarePen, User } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useUsers } from '../../users/hooks/use-users.hook';
import { useInspections } from '../hooks';
import { InspectionHistoryModal } from './inspection-history-modal.component';
import { InspectionStatusCards } from './inspection-status-cards.component';
import {
  type InspectionAction,
  type InspectionProposal,
  createInspectionsColumns,
  inspectionStatusConfig,
} from './inspections-table-columns';
import './inspections.styles.css';

const DEFAULT_SORT_FIELD = 'index_time';
const DEFAULT_SORT_TYPE = 'desc';

// Espelha os cards de status (`CARD_CONFIG` em inspection-status-cards) usando
// os mesmos valores de `apiStatus`. Card e dropdown compartilham o filtro
// `inspectionStatus`, então a seleção sincroniza nos dois sentidos.
const inspectionStatusOptions = [
  { label: 'Todos', value: '' },
  { label: 'Em Triagem', value: 'screening' },
  { label: 'Solicitada', value: InspectionStatus.Requested },
  { label: 'Em Andamento', value: InspectionStatus.InProgress },
  { label: 'Em Análise', value: InspectionStatus.UnderReview },
];

const INSPECTION_SORT_FIELD_BY_COLUMN_ID: Record<string, string> = {
  proposerName: 'Nome',
  brokerName: 'AgentName',
  product: 'ProductName',
  proposalNumber: 'ProposalNo',
  transmissionDate: 'TransmissionDate',
  proposalStatus: 'StatusPortal',
  inspectionStatus: 'PolicyStatusInspection',
};

const INSPECTION_COLUMN_ID_BY_SORT_FIELD: Record<string, string> = {
  Nome: 'proposerName',
  AgentName: 'brokerName',
  ProductName: 'product',
  ProposalNo: 'proposalNumber',
  TransmissionDate: 'transmissionDate',
  StatusPortal: 'proposalStatus',
  PolicyStatusInspection: 'inspectionStatus',
};

export function InspectionsContent({
  selectedBroker,
}: {
  selectedBroker: Broker | null;
}) {
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] =
    useState<InspectionProposal | null>(null);
  const navigate = useNavigate();

  const { filters, debouncedFilters, setFilters, registerFilter } = useFilters({
    search: f.string().withDefault('').withDebounce(500),
    pageNo: f.integer().withDefault(1),
    pageSize: f.integer().withDefault(10),
    inspectionStatus: f.string().withDefault(''),
    responsible: f.string().withDefault(''),
    productCode: f.string().withDefault(''),
    dateFrom: f.string().withDefault(''),
    dateTo: f.string().withDefault(''),
    sortField: f.string().withDefault(DEFAULT_SORT_FIELD),
    sortType: f.string().withDefault(DEFAULT_SORT_TYPE),
    statusPortal: f
      .integer()
      .withDefault(PortalStatus.ProposalPendingInspection),
  });
  const dataTableRef = useDataTablePageSync<InspectionProposal>(filters.pageNo);

  const { data: usersData } = useUsers({
    role: 'insurer.underwriter',
    limit: 1000,
  });

  const responsibleOptions = useMemo(() => {
    if (!usersData?.data) return [];
    return usersData.data
      .filter(user => user.identityId)
      .map(user => ({
        label: user.name,
        value: user.identityId!,
      }));
  }, [usersData]);

  const { data: products } = useProducts();
  const productOptions = useMemo(
    () => [
      { label: 'Todos os produtos', value: '' },
      ...(products ?? []).map(product => ({
        label: product.name,
        value: product.code,
      })),
    ],
    [products]
  );

  const { data, isLoading } = useInspections({
    pageNumber: debouncedFilters.pageNo,
    pageSize: debouncedFilters.pageSize,
    search: debouncedFilters.search,
    inspectionStatus: debouncedFilters.inspectionStatus || undefined,
    responsibleId: debouncedFilters.responsible || undefined,
    statusPortal: debouncedFilters.statusPortal,
    partyCode: selectedBroker?.code,
    productCode: debouncedFilters.productCode || undefined,
    proposalDateFrom: debouncedFilters.dateFrom || undefined,
    proposalDateTo: debouncedFilters.dateTo || undefined,
    sortField: filters.sortField,
    sortType: filters.sortType === 'desc' ? 'desc' : 'asc',
  });

  const handleFilterChange = (status: string) => {
    setFilters({ ...filters, inspectionStatus: status, pageNo: 1 });
  };

  const handleAction = (
    action: InspectionAction,
    proposal: InspectionProposal
  ) => {
    setSelectedProposal(proposal);
    if (action === 'history') {
      setHistoryModalOpen(true);
      return;
    }

    if (action === 'inspection') {
      void navigate({
        to: '/policies/$policyId/inspections/$inspectionId',
        params: {
          policyId: proposal.id,
          inspectionId: proposal.inspectionId || '_',
        },
      });
    }
  };

  const columns = createInspectionsColumns(handleAction);
  const inspections = (data?.data ?? []) as InspectionProposal[];
  const totalPages = data?.meta.totalPages ?? 0;
  const totalItems = data?.meta.total ?? 0;
  const sorting = useMemo(() => {
    const columnId = INSPECTION_COLUMN_ID_BY_SORT_FIELD[filters.sortField];

    if (!columnId) return [];

    return [
      {
        id: columnId,
        desc: filters.sortType === 'desc',
      },
    ];
  }, [filters.sortField, filters.sortType]);

  return (
    <>
      <div className='pt-4'>
        <InspectionStatusCards
          activeFilter={filters.inspectionStatus}
          onFilterChange={handleFilterChange}
          productCode={debouncedFilters.productCode || undefined}
          proposalDateFrom={debouncedFilters.dateFrom || undefined}
          proposalDateTo={debouncedFilters.dateTo || undefined}
          partyCode={selectedBroker?.code}
        />
      </div>

      <PageHeader.Filters>
        {/*
          São 5 filtros aqui (os 4 do padrão + "Responsável"), então este é o
          único listing que usa `flex-wrap`. Com wrap, o `w-full` de cada campo
          precisa ser cancelado em `md:` — um item de 100% ocupa a linha inteira
          sozinho e empilharia todos os filtros um por linha.
        */}
        <div className='flex flex-col gap-3 md:flex-1 md:flex-row md:flex-wrap md:items-center md:gap-4'>
          <SearchFilterField
            {...registerFilter('search')}
            placeholder='Buscar por nome, CNPJ, responsável ou email...'
            className='w-full md:w-auto md:max-w-sm md:flex-1'
          />
          <SelectFilterField
            {...registerFilter('inspectionStatus')}
            items={inspectionStatusOptions}
            placeholder='Status'
            className='w-full md:w-auto md:min-w-[200px]'
            onValueChange={handleFilterChange}
          />
          <SelectFilterField
            {...registerFilter('productCode')}
            items={productOptions}
            placeholder='Produto'
            className='w-full md:w-auto md:min-w-[200px]'
            onValueChange={value =>
              setFilters({ ...filters, productCode: value, pageNo: 1 })
            }
          />
          {/* Sem `min-w`: o controle do campo de data é fixo em 256px, então o
              slot precisa apenas abraçar esse tamanho. */}
          <DateRangeFilterField
            from={filters.dateFrom}
            to={filters.dateTo}
            placeholder='Selecione a data'
            className='w-full md:w-auto'
            onChange={range =>
              setFilters({
                ...filters,
                dateFrom: range.from,
                dateTo: range.to,
                pageNo: 1,
              })
            }
          />
          <SelectFilterField
            {...registerFilter('responsible')}
            items={responsibleOptions}
            placeholder='Responsável'
            onValueChange={value => {
              setFilters({ ...filters, responsible: value, pageNo: 1 });
            }}
            className='w-full md:w-48 md:flex-shrink-0'
          />
        </div>
      </PageHeader.Filters>

      <Page.Content className='min-w-0'>
        <div className='space-y-4 md:hidden'>
          {isLoading ? (
            Array.from({ length: Math.min(filters.pageSize, 3) }).map((_, index) => (
              <div
                key={index}
                className='h-64 animate-pulse rounded-3xl bg-alba-gray-200'
              />
            ))
          ) : (
            inspections.map(proposal => {
              const lastStatus = getLastInspectionStatusKey(proposal.inspectionStatus);
              const isTerminal = isInspectionTerminalStatus(lastStatus);
              const inspectionConfig = inspectionStatusConfig[
                lastStatus?.toLowerCase() as keyof typeof inspectionStatusConfig
              ] ?? {
                label: 'Desconhecido',
                colorScheme: 'gray' as const,
              };

              return (
                <article
                  key={`${proposal.id}-${proposal.inspectionId}`}
                  className='rounded-3xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'
                >
                  <div className='flex items-start justify-between gap-3'>
                    <div className='flex min-w-0 items-center gap-3'>
                      <Avatar.Root size='md' shape='circle' className='bg-alba-blue-500'>
                        <User className='w-4 h-4 text-white' />
                      </Avatar.Root>
                      <div className='min-w-0'>
                        <p className='truncate font-gotham font-bold text-alba-black-500'>
                          {proposal.proposerName}
                        </p>
                        <p className='mt-1 text-xs text-alba-gray-600'>
                          {proposal.proposerDocument}
                        </p>
                      </div>
                    </div>
                    <Menu.Root>
                      <Menu.Trigger />
                      <Menu.Content>
                        {!isTerminal && (
                          <Menu.Item
                            value='inspection'
                            icon={<SquarePen size={16} strokeWidth={1.6} />}
                            onSelect={() => handleAction('inspection', proposal)}
                          >
                            Inspeção
                          </Menu.Item>
                        )}
                        <Menu.Item
                          value='history'
                          icon={<History size={16} strokeWidth={1.6} />}
                          onSelect={() => handleAction('history', proposal)}
                        >
                          Histórico
                        </Menu.Item>
                      </Menu.Content>
                    </Menu.Root>
                  </div>

                  <div className='mt-4 space-y-2'>
                    <div className='flex items-center justify-between gap-3'>
                      <p className='text-sm font-semibold text-alba-black-500'>
                        Corretor
                      </p>
                      <p className='truncate text-sm text-alba-gray-700'>
                        {proposal.brokerName}
                      </p>
                    </div>
                    <div className='flex items-center justify-between gap-3'>
                      <p className='text-sm font-semibold text-alba-black-500'>
                        Produto
                      </p>
                      <p className='truncate text-sm text-alba-gray-700'>
                        {proposal.product}
                      </p>
                    </div>
                    <div className='flex items-center justify-between gap-3'>
                      <p className='text-sm font-semibold text-alba-black-500'>
                        N° da Proposta
                      </p>
                      <div className='flex flex-col items-end gap-0.5 text-right'>
                        <p className='text-sm text-alba-gray-700'>
                          {proposal.proposalNumber}
                        </p>
                        <div className='flex items-center gap-1 text-xs text-alba-gray-600'>
                          <CalendarDays className='h-3.5 w-3.5' />
                          <span>{formatDateToBrazil(proposal.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                    <div className='flex items-center justify-between gap-3'>
                      <p className='text-sm font-semibold text-alba-black-500'>
                        Transmissão
                      </p>
                      <div className='flex items-center gap-1 text-sm text-alba-gray-700'>
                        <CalendarDays className='h-3.5 w-3.5 text-alba-gray-600' />
                        <span>{formatDateToBrazil(proposal.transmissionDate)}</span>
                      </div>
                    </div>
                    <div className='flex items-center justify-between gap-3'>
                      <p className='text-sm font-semibold text-alba-black-500'>
                        Status
                      </p>
                      <Tag.Root
                        variant='outline'
                        colorScheme={inspectionConfig.colorScheme}
                        size='sm'
                      >
                        <Tag.Label>{inspectionConfig.label}</Tag.Label>
                      </Tag.Root>
                    </div>
                  </div>
                </article>
              );
            })
          )}

          {!isLoading && !inspections.length && (
            <div className='rounded-3xl bg-white p-6 text-center text-alba-gray-600 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
              Nenhuma proposta em inspeção encontrada.
            </div>
          )}

          {totalPages > 0 && (
            <div className='flex justify-center'>
              <Pagination
                count={totalPages * filters.pageSize}
                page={filters.pageNo}
                pageSize={filters.pageSize}
                totalItems={totalItems}
                pageSizeOptions={[5, 10, 20, 30, 40, 50]}
                onPageChange={page =>
                  setFilters({ ...filters, pageNo: page.page, pageSize: page.pageSize })
                }
                onPageSizeSelect={pageSize =>
                  setFilters({ ...filters, pageNo: 1, pageSize })
                }
              />
            </div>
          )}
        </div>

        <div className='hidden min-w-0 overflow-hidden rounded-3xl md:block'>
          <DataTable
            ref={dataTableRef}
            loading={isLoading}
            columns={columns}
            pageSizeOptions={[5, 10, 20, 30, 40, 50]}
            totalPages={totalPages}
            data={inspections}
            pageSize={filters.pageSize}
            totalItems={totalItems}
            className='inspections-data-table min-w-0'
            manualSorting
            sorting={sorting}
            onSortChange={nextSorting => {
              const nextSort = nextSorting[0];
              const sortField = nextSort
                ? INSPECTION_SORT_FIELD_BY_COLUMN_ID[nextSort.id]
                : DEFAULT_SORT_FIELD;

              setFilters({
                ...filters,
                pageNo: 1,
                sortField: sortField ?? DEFAULT_SORT_FIELD,
                sortType: nextSort?.desc ? 'desc' : DEFAULT_SORT_TYPE,
              });
            }}
            onPageChange={(page, pageSize) =>
              setFilters({ ...filters, pageNo: page, pageSize })
            }
          />
        </div>
      </Page.Content>

      <InspectionHistoryModal
        isOpen={historyModalOpen}
        policyId={selectedProposal?.id}
        inspectionId={selectedProposal?.inspectionId}
        listingInspectionStatus={selectedProposal?.inspectionStatus}
        onClose={() => {
          setHistoryModalOpen(false);
          setSelectedProposal(null);
        }}
      />
    </>
  );
}
